import { z, type ZodTypeAny } from 'zod'
import { enrich } from '@i18n/localizeHelper'
import en from '@i18n/languages/en'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type FormValidationMessages = {
  required: string
  minLength: string // uses {0} for the limit
  maxLength: string // uses {0} for the limit
  min: string // uses {0} for the limit
  max: string // uses {0} for the limit
  invalidEmail: string
  invalidUrl: string
  invalidFormat: string
  mustBeNumber: string
  mustBeInteger: string
}

export type SchemaToZodOptions = {
  /** Only include these top-level field names. */
  pick?: string[]
  /** Exclude these top-level field names. */
  omit?: string[]
  /** Override validation messages. Defaults to English strings. */
  messages?: Partial<FormValidationMessages>
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

export const DEFAULT_VALIDATION_MESSAGES: FormValidationMessages = {
  ...en.app.form.validation,
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Format strings that are auto-generated by the Based backend — skip them. */
const SKIP_ALIAS_FORMATS = new Set(['UUID', 'slug', 'lowercase'])

/** Build a string Zod schema applying format, min/max constraints. */
function buildStringSchema(
  format: string | undefined,
  min: number | undefined,
  max: number | undefined,
  required: boolean,
  msgs: FormValidationMessages,
): ZodTypeAny {
  let s: z.ZodString = z.string()

  if (required) s = s.min(1, msgs.required)

  if (format === 'email') s = s.email(msgs.invalidEmail)
  else if (format === 'URL') s = s.url(msgs.invalidUrl)

  if (min !== undefined) s = s.min(min, enrich(msgs.minLength, min))

  if (max !== undefined) s = s.max(max, enrich(msgs.maxLength, max))

  return required ? s : s.optional()
}

/** Build a number Zod schema applying int/min/max constraints. */
function buildNumberSchema(
  isInt: boolean,
  hardMin: number | undefined,
  hardMax: number | undefined,
  min: number | undefined,
  max: number | undefined,
  required: boolean,
  msgs: FormValidationMessages,
): ZodTypeAny {
  let s = z.number({ message: msgs.mustBeNumber })

  if (isInt) s = s.int(msgs.mustBeInteger)
  if (hardMin !== undefined) s = s.min(hardMin)
  if (hardMax !== undefined) s = s.max(hardMax)
  if (min !== undefined) s = s.min(min, enrich(msgs.min, min))
  if (max !== undefined) s = s.max(max, enrich(msgs.max, max))

  return required ? s : s.optional()
}

/** Convert a single Based schema field definition to a Zod type. Returns null
 *  for fields that should be skipped (refs, collections, auto-timestamps…). */
function convertField(
  def: unknown,
  msgs: FormValidationMessages,
): ZodTypeAny | null {
  // ── String shorthands ──────────────────────────────────────────────────────
  if (def === 'string' || def === 'text') return z.string().optional()
  if (def === 'boolean') return z.boolean().optional()

  // ── Numeric shorthands ─────────────────────────────────────────────────────
  if (def === 'number')
    return buildNumberSchema(
      false,
      undefined,
      undefined,
      undefined,
      undefined,
      false,
      msgs,
    )
  if (def === 'uint8')
    return buildNumberSchema(true, 0, 255, undefined, undefined, false, msgs)
  if (def === 'uint16')
    return buildNumberSchema(true, 0, 65535, undefined, undefined, false, msgs)
  if (def === 'uint32')
    return buildNumberSchema(
      true,
      0,
      4294967295,
      undefined,
      undefined,
      false,
      msgs,
    )
  if (def === 'int8')
    return buildNumberSchema(true, -128, 127, undefined, undefined, false, msgs)
  if (def === 'int16')
    return buildNumberSchema(
      true,
      -32768,
      32767,
      undefined,
      undefined,
      false,
      msgs,
    )
  if (def === 'int32')
    return buildNumberSchema(
      true,
      undefined,
      undefined,
      undefined,
      undefined,
      false,
      msgs,
    )
  if (def === 'timestamp')
    return z.number({ message: msgs.mustBeNumber }).optional()

  // ── Enum array shorthand (e.g. Object.values(SOME_CONST)) ─────────────────
  if (Array.isArray(def)) {
    const values = def.map(String)
    if (values.length === 0) return z.string().optional()
    return z.enum(values as [string, ...string[]]).optional()
  }

  if (typeof def !== 'object' || def === null) return null

  const d = def as Record<string, unknown>

  // ── Skip refs and collections ──────────────────────────────────────────────
  if ('ref' in d || 'items' in d) return null

  // ── Skip auto-managed timestamps ───────────────────────────────────────────
  if (d.type === 'timestamp' && 'on' in d) return null

  // ── Nested object (props) ─────────────────────────────────────────────────
  if ('props' in d && typeof d.props === 'object' && d.props !== null) {
    return convertSchema(d.props as Record<string, unknown>, msgs)
  }

  const type = d.type as string | undefined
  const required = d.required === true
  const min = d.min as number | undefined
  const max = d.max as number | undefined
  const format = d.format as string | undefined

  // ── Alias (unique identifier or special format) ────────────────────────────
  if (type === 'alias') {
    if (format && SKIP_ALIAS_FORMATS.has(format)) return null
    return buildStringSchema(format, min, max, required, msgs)
  }

  // ── String / text ─────────────────────────────────────────────────────────
  if (type === 'string' || type === 'text') {
    return buildStringSchema(format, min, max, required, msgs)
  }

  // ── Boolean ───────────────────────────────────────────────────────────────
  if (type === 'boolean') return required ? z.boolean() : z.boolean().optional()

  // ── Enum ──────────────────────────────────────────────────────────────────
  if (type === 'enum') {
    const enumValues = (d.enum as (string | number | boolean)[]).map(String)
    if (enumValues.length === 0) return z.string().optional()
    const schema = z.enum(enumValues as [string, ...string[]])
    return required ? schema : schema.optional()
  }

  // ── Number variants ───────────────────────────────────────────────────────
  if (type === 'number')
    return buildNumberSchema(
      false,
      undefined,
      undefined,
      min,
      max,
      required,
      msgs,
    )
  if (type === 'uint8')
    return buildNumberSchema(true, 0, 255, min, max, required, msgs)
  if (type === 'uint16')
    return buildNumberSchema(true, 0, 65535, min, max, required, msgs)
  if (type === 'uint32')
    return buildNumberSchema(true, 0, 4294967295, min, max, required, msgs)
  if (type === 'int8')
    return buildNumberSchema(true, -128, 127, min, max, required, msgs)
  if (type === 'int16')
    return buildNumberSchema(true, -32768, 32767, min, max, required, msgs)
  if (type === 'int32')
    return buildNumberSchema(
      true,
      undefined,
      undefined,
      min,
      max,
      required,
      msgs,
    )

  // ── Timestamp ─────────────────────────────────────────────────────────────
  if (type === 'timestamp')
    return z.number({ message: msgs.mustBeNumber }).optional()

  return null
}

function convertSchema(
  schema: Record<string, unknown>,
  msgs: FormValidationMessages,
): z.ZodObject<Record<string, ZodTypeAny>> {
  const shape: Record<string, ZodTypeAny> = {}
  for (const [key, def] of Object.entries(schema)) {
    const fieldSchema = convertField(def, msgs)
    if (fieldSchema !== null) {
      shape[key] = fieldSchema
    }
  }
  return z.object(shape)
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Converts a `@based/schema` field definition object into a Zod schema for
 * use with react-hook-form's `zodResolver`.
 *
 * One source of truth: the same constraints defined in the Based schema
 * (required, min, max, format) are enforced here on the frontend.
 *
 * @example
 * ```ts
 * import { user } from '@schema/user'
 * import { schemaToZod } from '@app/components/core/form/schema/schemaToZod'
 *
 * const schema = schemaToZod(user, {
 *   pick: ['firstName', 'lastName', 'email'],
 *   messages: useTranslations().app.form.validation,
 * })
 * ```
 */
export function schemaToZod(
  basedSchema: Record<string, unknown>,
  options: SchemaToZodOptions = {},
): z.ZodObject<Record<string, ZodTypeAny>> {
  const msgs: FormValidationMessages = {
    ...DEFAULT_VALIDATION_MESSAGES,
    ...options.messages,
  }

  let fields = basedSchema
  const { pick, omit } = options

  if (pick) {
    fields = Object.fromEntries(
      Object.entries(fields).filter(([k]) => pick.includes(k)),
    )
  }

  if (omit) {
    fields = Object.fromEntries(
      Object.entries(fields).filter(([k]) => !omit.includes(k)),
    )
  }

  return convertSchema(fields, msgs)
}
